import React from 'react';
import { useIntl } from 'react-intl';
import { Modal } from 'antd';
import styled from 'styled-components';

import SuccessGif from '@assets/success.gif';
import FormButton from '@components/form/form-button';
import Image from '@domain/image';
import { abortScanRequest } from '@services/mrz';
import { useVismaConfiguration } from '@services/visitor-management';

const HeaderText = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: ${({ theme }) => theme.primary};
  margin: 10px 0 24px 0;
`;

const ModalText = styled.div`
  font-size: 16px;
  font-weight: 400;
  color: ${({ theme }) => theme.textSecondary};
  margin: 20px 0;
  padding-left: 16px;
  & > ol > li > b {
    font-size: 16px;
    font-weight: 600;
    color: ${({ theme }) => theme.primary};
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 1em;
`;

const ErrorText = styled.label`
  font-size: 16px;
  font-weight: 500;
  color: ${({ theme }) => theme.error};
`;

const SuccessContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

interface ScanStatus {
  isSuccess?: boolean;
  isMRZerror?: boolean;
  isCantReaded?: boolean;
  isScanning?: boolean;
  errorNumber?: number;
  errorCode?: string;
}

interface MrzScanModalProps {
  open: boolean;
  closeModal: () => void;
  handleScan: () => void;
  scanStatus?: ScanStatus;
}

function MrzScanModal({
  open,
  closeModal,
  handleScan,
  scanStatus
}: MrzScanModalProps) {
  const intl = useIntl();
  const { configuration } = useVismaConfiguration();
  const { scan_popup_title, scan_popup_description, scan_popup_image } =
    configuration?.personalData ?? {};
  const { button_close, button_scan } = configuration?.generalInformation ?? {};

  return (
    <Modal open={open} onCancel={closeModal} footer={null} width="40vw">
      {scanStatus?.isSuccess ? (
        <>
          <SuccessContainer>
            <img src={SuccessGif} alt="scan-icon" width={300} height={300} />
          </SuccessContainer>
          <HeaderText style={{ textAlign: 'center' }}>
            {intl.formatMessage({ id: 'YOUR_DOCUMENT_SUCCESFULLY_SCANNED' })}
          </HeaderText>
        </>
      ) : (
        <>
          {scan_popup_image?.id && (
            <Image
              id={scan_popup_image.id as string}
              alt="scan-icon"
              style={{ width: '100%' }}
            />
          )}
          <HeaderText>{scan_popup_title}</HeaderText>
          <ModalText style={{ whiteSpace: 'pre-line' }}>
            {scan_popup_description}
          </ModalText>
          {scanStatus?.isMRZerror ? (
            <ErrorText>
              {intl.formatMessage({
                id:
                  'ERROR_MRZ_' +
                  scanStatus?.errorNumber +
                  '_' +
                  scanStatus?.errorCode
              })}
            </ErrorText>
          ) : scanStatus?.isCantReaded ? (
            <ErrorText>{intl.formatMessage({ id: 'MRZ_CANT_READ' })}</ErrorText>
          ) : null}
          <ButtonContainer>
            <FormButton
              withLeftArrow
              type="default"
              variant="outlined"
              onClick={() => {
                abortScanRequest();
                closeModal();
              }}
            >
              {button_close ?? intl.formatMessage({ id: 'CLOSE' })}
            </FormButton>
            <FormButton
              withRightArrow
              type="primary"
              onClick={handleScan}
              disabled={scanStatus?.isScanning}
              style={{ marginLeft: 16 }}
            >
              {scanStatus?.isScanning
                ? intl.formatMessage({ id: 'SCANNING' })
                : (button_scan ?? intl.formatMessage({ id: 'SCAN' }))}
            </FormButton>
          </ButtonContainer>
        </>
      )}
    </Modal>
  );
}

export default MrzScanModal;
